import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { journeyStages } from '../../data/crm';
import { StagePanel } from './StagePanel';
const ease = [0.23, 1, 0.32, 1] as const;
export function CustomerJourney() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mobileOpen, setMobileOpen] = useState<number | null>(0);
  const active = journeyStages[activeIndex];
  const progress = activeIndex / (journeyStages.length - 1) * 100;
  return <section id="journey" aria-labelledby="journey-title" className="bg-white px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-id="element-128" data-mp="rga363is7qsi">
      <div className="mx-auto max-w-7xl" data-id="element-129" data-mp="csf8wip4ledb">
        <div className="max-w-2xl" data-id="element-130" data-mp="hriatu9t74h2">
          <h2 id="journey-title" className="text-3xl font-extrabold tracking-tight text-brand-black sm:text-4xl lg:text-5xl" data-id="element-131" data-mp="hqgzrpssmhzc">
            One system for the whole customer journey
          </h2>
          <p className="mt-4 text-lg text-gray-600" data-id="element-132" data-mp="1a8urr8rg79qi">From first click to five-star review to repeat customer.</p>
        </div>

        {/* Desktop: horizontal journey tabs */}
        <div className="mt-14 hidden lg:block" data-id="element-133" data-mp="3ulieuyg57w82">
          <div role="tablist" aria-label="Customer journey" className="relative grid grid-cols-5" data-id="element-134" data-mp="3umti8x5of1yn">
            <div className="absolute left-[10%] right-[10%] top-5 h-0.5 bg-brand-light" aria-hidden="true" data-id="element-135" data-mp="d5tnyh02yts1">
              <motion.div className="h-full bg-brand-red" animate={{
              width: `${progress}%`
            }} transition={{
              duration: 0.3,
              ease
            }} data-id="element-136" data-mp="8bpftuf2wfpd" />
            </div>
            {journeyStages.map((stage, i) => {
            const isActive = i === activeIndex;
            const isDone = i < activeIndex;
            return <button key={stage.id} type="button" role="tab" id={`tab-${stage.id}`} aria-selected={isActive} aria-controls={`panel-${stage.id}`} onClick={() => setActiveIndex(i)} className="group relative flex flex-col items-center gap-3 rounded-md pb-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red" data-id="element-137" data-mp="r51mr8sbzzvp" data-mpi={i}>
                  <span className={`relative z-10 flex h-10 w-10 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors duration-200 ease-out ${isActive ? 'border-brand-red bg-brand-red text-white' : isDone ? 'border-brand-red bg-white text-brand-red' : 'border-brand-light bg-white text-brand-gray group-hover:border-brand-gray'}`} data-id="element-138" data-mp="3rivt4k2czxrn">
                    {i + 1}
                  </span>
                  <span className={`text-base font-bold transition-colors duration-150 ease-out ${isActive ? 'text-brand-red' : 'text-brand-black group-hover:text-brand-red'}`} data-id="element-139" data-mp="o5b7x88di0fv">
                    {stage.label}
                  </span>
                </button>;
          })}
          </div>

          <div className="mt-12 rounded-2xl border border-gray-200 p-10" data-id="element-140" data-mp="23nh7h1xnyfn4">
            <AnimatePresence mode="wait" initial={false} data-id="element-141" data-mp="1yo4povlnfyre">
              <motion.div key={active.id} role="tabpanel" id={`panel-${active.id}`} aria-labelledby={`tab-${active.id}`} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -4
            }} transition={{
              duration: 0.2,
              ease
            }} data-id="element-142" data-mp="1yppee4la8nhc">
                <StagePanel stage={active} data-id="element-143" data-mp="2hkeae1r23nkd" />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Mobile: stacked accordion with a vertical journey line */}
        <ol className="relative mt-10 lg:hidden" data-id="element-144" data-mp="3a91p1ehaveoy">
          <span className="absolute bottom-6 left-5 top-6 w-0.5 bg-brand-light" aria-hidden="true" data-id="element-145" data-mp="2usv7xn4hij8r" />
          {journeyStages.map((stage, i) => {
          const isOpen = mobileOpen === i;
          return <li key={stage.id} className="relative pb-4" data-id="element-146" data-mp="2kw8pt63jm6af" data-mpi={i}>
                <button type="button" aria-expanded={isOpen} onClick={() => setMobileOpen(isOpen ? null : i)} className="flex w-full items-start gap-4 rounded-md text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-red" data-id="element-147" data-mp="2pts240gok8zq">
                  <span className={`relative z-10 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold transition-colors duration-200 ease-out ${isOpen ? 'border-brand-red bg-brand-red text-white' : 'border-brand-light bg-white text-brand-gray'}`} data-id="element-148" data-mp="33srp73ojofts">
                    {i + 1}
                  </span>
                  <span className="flex-1 pt-1" data-id="element-149" data-mp="1psdfklp7cxxc">
                    <span className="block text-xs font-bold text-brand-red" data-id="element-150" data-mp="1kvn122t1qae1">{stage.label}</span>
                    <span className="mt-0.5 block text-lg font-extrabold leading-snug text-brand-black" data-id="element-151" data-mp="1zkj7xwjd9hd2">{stage.headline}</span>
                    <span className="mt-1 block text-sm text-gray-600" data-id="element-152" data-mp="1um9b7hhzu8pc">{stage.sub}</span>
                  </span>
                  <ChevronDownIcon className={`mt-2 h-5 w-5 flex-shrink-0 text-brand-gray transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-153" data-mp="16vvrce9kbg5u" />
                </button>
                <AnimatePresence initial={false} data-id="element-154" data-mp="1bu71kq3rrsol">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.25,
                ease
              }} className="overflow-hidden pl-14" data-id="element-155" data-mp="2rao0wjl6zcpw">
                      <div className="pb-4 pt-6" data-id="element-156" data-mp="1ovy9z2m66g83">
                        <StagePanel stage={stage} showHeading={false} data-id="element-157" data-mp="1twckqsf5o49t" />
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>;
        })}
        </ol>
      </div>
    </section>;
}